import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Users, Wallet, ShoppingBag, ArrowDownToLine, ArrowRight } from 'lucide-react';
import api from '../../../services/api';
import { useI18n } from '../../../i18n/I18nProvider';
import { errorText } from '../../../lib/errors';
import { StatCard, Card, CardHeader, CardBody, TrendChart, StatusBadge, LoadingBlock } from '../../../ui';

const growth = (curr, prev) => (prev > 0 ? Math.round(((curr - prev) / prev) * 100) : null);

/** Состояние кассы по данным синхронизации/проверки */
export function posHealthFromStats(pos) {
  if (!pos?.configured) return 'unconfigured';
  const s = pos.sync;
  if (!s) return 'unknown';
  const ok = [s.last_success_at, s.last_check_ok === 1 ? s.last_check_at : null].filter(Boolean).sort().pop();
  const bad = [s.last_error_at, s.last_check_ok === 0 ? s.last_check_at : null].filter(Boolean).sort().pop();
  if (s.pos_ok === 0 || (bad && (!ok || bad > ok))) return 'error';
  // Касса отвечает, но системная служба (история транзакций) недоступна
  if (s.system_ok === 0) return 'degraded';
  return ok || s.pos_ok === 1 ? 'ok' : 'unknown';
}

export function useCompanyStats(tenantId) {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let alive = true;
    api.get(`/admin/tenants/${tenantId}/stats`)
      .then((res) => { if (alive) setStats(res.data.data); })
      .catch((err) => toast.error(errorText(err, t('common.error'))));
    return () => { alive = false; };
  }, [tenantId, version, t]);
  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return [stats, reload];
}

export default function OverviewTab({ tenant, onOpenTab }) {
  const { t, fmt } = useI18n();
  const [stats] = useCompanyStats(tenant.id);
  if (!stats) return <LoadingBlock />;

  const health = posHealthFromStats(stats.pos);
  const sync = stats.pos.sync;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('company.kpi.employees')}
          value={fmt.number(stats.employees.total)}
          hint={t('company.kpi.employeesHint', { registered: fmt.number(stats.employees.registered), active7: fmt.number(stats.employees.active7) })}
          icon={Users}
        />
        <StatCard
          label={t('company.kpi.balance')}
          value={fmt.money(stats.balance)}
          hint={t('company.kpi.balanceHint', { cards: fmt.number(stats.cards) })}
          icon={Wallet}
        />
        <StatCard
          label={t('company.kpi.spend')}
          value={fmt.money(stats.spend30.sum)}
          delta={growth(stats.spend30.sum, stats.spend30.prev)}
          hint={growth(stats.spend30.sum, stats.spend30.prev) !== null ? t('overview.spendHint') : null}
          icon={ShoppingBag}
          tone="brand"
        />
        <StatCard label={t('company.kpi.topup')} value={fmt.money(stats.topup30)} icon={ArrowDownToLine} />
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader title={t('overview.chartTitle')} description={t('company.last30')} />
          <CardBody>
            <TrendChart
              data={stats.trend}
              series={[{ key: 'spend', label: t('overview.seriesSpend') }, { key: 'topup', label: t('overview.seriesTopup') }]}
              formatValue={(v) => fmt.number(v, { notation: 'compact', maximumFractionDigits: 1 })}
              formatDate={(d) => fmt.date(d)}
            />
          </CardBody>
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader
              title={t('company.posCard')}
              actions={<button type="button" onClick={() => onOpenTab('pos')} className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">{t('overview.open')}</button>}
            />
            <CardBody className="space-y-3 text-sm">
              <StatusBadge kind="pos" value={health} />
              <InfoRow label={t('company.lastSync')} value={sync?.last_run_at ? `${fmt.relative(sync.last_run_at)} · ${t('company.syncedCount', { synced: sync.last_synced ?? 0, failed: sync.last_failed ?? 0 })}` : t('common.never')} />
              <InfoRow label={t('company.lastCheck')} value={sync?.last_check_at ? fmt.relative(sync.last_check_at) : t('common.never')} />
              {health === 'error' && (sync?.pos_error || sync?.last_check_error || sync?.last_error) && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-300">
                  {sync.pos_down_since && <span className="mb-1 block">{t('company.posDown', { since: fmt.dateTime(sync.pos_down_since) })}</span>}
                  <span className="font-mono">{sync.pos_error || sync.last_check_error || sync.last_error}</span>
                </p>
              )}
              {health === 'degraded' && (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                  {t('company.historyDown', { since: fmt.dateTime(sync.system_down_since) })}
                  {sync.system_error && <span className="mt-1 block font-mono opacity-80">{sync.system_error}</span>}
                </p>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={t('company.billingCard')}
              actions={<button type="button" onClick={() => onOpenTab('billing')} className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">{t('overview.open')}</button>}
            />
            <CardBody className="space-y-3 text-sm">
              <StatusBadge kind="billing" value={stats.billing.state} />
              <InfoRow label={t('companies.plan')} value={`${t(`plans.${stats.billing.plan}`)} · ${fmt.money(stats.billing.plan_price, stats.billing.currency)} ${t(`cycles.${stats.billing.billing_cycle}`).toLowerCase()}`} />
              <InfoRow label={t('company.nextPayment')} value={fmt.date(stats.billing.billing_next_date)} />
            </CardBody>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader title={t('company.topVenues')} />
        {stats.top_venues.length ? (
          <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {stats.top_venues.map((v, i) => {
              const max = stats.top_venues[0].sum || 1;
              return (
                <li key={v.name} className="flex items-center gap-4 px-5 py-3">
                  <span className="tabular w-5 text-xs text-zinc-400">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">{v.name}</p>
                      <p className="tabular shrink-0 text-sm text-zinc-900 dark:text-zinc-100">{fmt.money(v.sum)}</p>
                    </div>
                    <div className="mt-1.5 flex items-center gap-3">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                        <div className="h-full rounded-full bg-[#2a78d6] dark:bg-[#3987e5]" style={{ width: `${Math.max(4, (v.sum / max) * 100)}%` }} />
                      </div>
                      <span className="tabular shrink-0 text-xs text-zinc-500">{t('company.visits', { count: v.visits })}</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <CardBody><p className="text-sm text-zinc-500">{t('company.noVenues')}</p></CardBody>
        )}
      </Card>

      <button
        type="button"
        onClick={() => onOpenTab('team')}
        className="flex w-full items-center justify-between rounded-xl border border-zinc-200 bg-white px-5 py-4 text-left shadow-card hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:bg-zinc-800/60"
      >
        <span className="text-sm text-zinc-700 dark:text-zinc-300">
          {t('company.tabs.team')}: <span className="tabular font-medium text-zinc-900 dark:text-zinc-100">{stats.team.active}</span>
          <span className="text-zinc-400"> / {stats.team.total}</span>
        </span>
        <ArrowRight className="h-4 w-4 text-zinc-400" />
      </button>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-zinc-500 dark:text-zinc-400">{label}</span>
      <span className="tabular text-right text-zinc-900 dark:text-zinc-100">{value}</span>
    </div>
  );
}
